import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { 
  FolderOpen, FileText, HelpCircle, Lightbulb, 
  Lock, ArrowRight, Crown, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ParentPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // Allow access without login
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const tools = [
    {
      id: 'resources',
      name: 'My Resources',
      description: 'Access your uploaded files and saved materials',
      icon: FolderOpen,
      route: '/resources',
      color: 'from-blue-500 to-cyan-500',
      requiresAuth: true
    },
    {
      id: 'activities',
      name: 'AI Activity Suggester',
      description: 'Get fun learning activities for home',
      icon: Lightbulb,
      route: '/parent/activity-suggester',
      color: 'from-yellow-500 to-orange-500',
      requiresAuth: false
    },
    {
      id: 'explainer',
      name: 'Topic Explainer',
      description: 'Get simple explanations for any topic',
      icon: HelpCircle,
      route: '/parent/topic-explainer',
      color: 'from-indigo-500 to-blue-500',
      requiresAuth: false
    },
    {
      id: 'teacher-tips',
      name: 'Teacher Tips',
      description: 'Browse educational resources and teaching ideas',
      icon: FileText,
      route: '/teaching-tips',
      color: 'from-purple-500 to-pink-500',
      requiresAuth: false
    }
  ];

  const handleToolClick = (tool) => {
    if (tool.requiresAuth && !user) {
      toast.error('Please sign in to access this feature');
      navigate('/login?redirect=' + tool.route);
      return;
    }
    
    // Check if user has parent subscription (except for the portal hub itself)
    if (user && user.subscription_tier !== 'parent' && user.subscription_tier !== 'plus' && user.subscription_tier !== 'pro') {
      toast.error('Please upgrade to access Parent Portal tools');
      navigate('/parent/pricing');
      return;
    }
    
    navigate(tool.route);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Navigation />
      
      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
              <Sparkles className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Parent Portal
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Tools to support your child's learning journey at home
            </p>
            {user && (
              <p className="text-sm text-blue-600 font-medium mt-2">
                Welcome back, {user.name}!
              </p>
            )}
          </div>

          {/* Tools Grid - Mobile Optimized */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {tools.map((tool) => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.id}
                  onClick={() => handleToolClick(tool)}
                  className="group relative bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 text-left border-2 border-transparent hover:border-blue-200 active:scale-95"
                >
                  {/* Gradient Background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${tool.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity`}></div>
                  
                  {/* Content */}
                  <div className="relative">
                    {/* Icon */}
                    <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${tool.color} rounded-xl mb-4 shadow-md group-hover:shadow-lg transition-shadow`}>
                      <Icon className="w-7 h-7 text-white" strokeWidth={2} />
                    </div>

                    {/* Lock Badge for Auth Required */}
                    {tool.requiresAuth && !user && (
                      <div className="absolute top-0 right-0 bg-gray-100 rounded-full p-2">
                        <Lock className="w-4 h-4 text-gray-500" />
                      </div>
                    )}

                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {tool.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">
                      {tool.description}
                    </p>

                    {/* Arrow */}
                    <div className="flex items-center text-blue-600 text-sm font-medium">
                      <span className="mr-1">Open</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Info Cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Subscription Status / Upgrade Prompt */}
            {user ? (
              user.subscription_tier === 'parent' || user.subscription_tier === 'plus' || user.subscription_tier === 'pro' ? (
                <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-green-600" />
                    Active Subscription
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    You have full access to all Parent Portal tools!
                  </p>
                  <p className="text-xs text-green-700 font-medium">
                    Tier: {user.subscription_tier.toUpperCase()}
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Unlock All Tools
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Get unlimited access to all Parent Portal tools for $10/month
                  </p>
                  <button
                    onClick={() => navigate('/parent/pricing')}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-700 transition-colors text-sm inline-flex items-center gap-2"
                  >
                    <Crown className="w-4 h-4" />
                    Subscribe Now
                  </button>
                </div>
              )
            ) : (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Get Started
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Sign in and subscribe for $10/month to access all tools
                </p>
                <button
                  onClick={() => navigate('/parent/pricing')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                >
                  View Pricing
                </button>
              </div>
            )}

            {/* Help Card */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Need Help?
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Use the Activity Suggester and Topic Explainer to support your child's learning at home.
              </p>
              <a
                href="/teaching-tips"
                className="text-purple-600 font-medium hover:text-purple-700 transition-colors text-sm inline-flex items-center"
              >
                View Tips & Resources
                <ArrowRight className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ParentPortal;
